import en from './locales/en.json';

/**
 * One UI string, in the active language.
 *
 * Resolution is dictionary → English → the key itself. Returning the key is
 * deliberate: a missing string shows up as `projects.title` on the page, which
 * is obvious in review, rather than as a blank space that looks like a design
 * choice.
 *
 * An empty translation counts as missing. Locale files are written by hand and
 * an empty string is always an oversight, never an intention.
 */

const warned = new Set();

function warnOnce(key) {
  if (process.env.NODE_ENV === 'production' || warned.has(key)) return;
  warned.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] no string for "${key}"`);
}

const present = (value) => typeof value === 'string' && value.trim() !== '';

/** dict -> English -> null, for one exact key. No key-itself fallback here —
 * that only applies to the final candidate, in `translate` below. */
function lookup(dict, key) {
  if (dict && present(dict[key])) return dict[key];
  const fallback = en.ui[key];
  return present(fallback) ? fallback : null;
}

export function translate(dict, key, vars, locale = 'en') {
  // A numeric `count` selects a plural category: "5 files" and "1 file" are
  // different sentences in English, and some of the languages this site ships
  // (Arabic: six categories, Russian: three) need more than a one/other
  // ternary can express. Intl.PluralRules is the platform's own answer to
  // "which category", so the catalog only has to supply the forms a language
  // actually distinguishes — `_other` is the required catch-all every locale
  // must define, and Intl guarantees `select()` never returns a category
  // absent from that fallback chain without one existing.
  let resolvedKey = key;
  let value = null;

  if (vars && typeof vars.count === 'number') {
    const category = new Intl.PluralRules(locale).select(vars.count);
    const candidates = [`${key}_${category}`, `${key}_other`, key];
    for (const candidate of candidates) {
      value = lookup(dict, candidate);
      if (value !== null) {
        resolvedKey = candidate;
        break;
      }
    }
  } else {
    value = lookup(dict, key);
  }

  if (value === null) {
    warnOnce(resolvedKey);
    return key;
  }

  if (!vars) return value;

  return Object.entries(vars).reduce(
    (text, [name, replacement]) => text.split(`{${name}}`).join(String(replacement)),
    value
  );
}
